import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { OrganizationSchema, WebPageSchema, FAQSchema } from '@/components/feature/StructuredData';

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone_alt: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotValue = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypotValue) {
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '', phone_alt: '' });
      return;
    }

    if (!formData.name || !formData.email || !formData.message) {
      setStatus('error');
      setErrorMsg('请填写所有必填字段');
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }

    setStatus('submitting');

    try {
      const payload = new FormData(form);
      payload.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9orjn0senbnioleskjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', phone_alt: '' });
      } else if (serverMsg.includes('spam') || (typeof responseText === 'string' && responseText.includes('spam'))) {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后重试');
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all cursor-text"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all cursor-text"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          type="text"
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all cursor-text"
          placeholder="请输入主题"
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={4}
          maxLength={500}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none cursor-text"
          placeholder="请输入您的留言（500字以内）"
        />
        <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
      </div>
      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="honeypot-field"
        value={formData.phone_alt}
        onChange={(e) => setFormData({ ...formData, phone_alt: e.target.value })}
      />
      {status === 'error' && errorMsg && (
        <p className="text-sm text-red-500">{errorMsg}</p>
      )}
      {status === 'success' && (
        <p className="text-sm text-green-600">您的留言已成功提交，我们会尽快回复！</p>
      )}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交留言
          </>
        )}
      </button>
    </form>
  );
}

export default function About() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const teamMembers = [
    {
      name: '李明浩',
      role: '创始人 & CEO',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20man%20in%20casual%20business%20attire%2C%20warm%20friendly%20smile%2C%20modern%20office%20background%2C%20clean%20portrait%20photography%2C%20soft%20natural%20lighting%2C%20minimalist%20composition%2C%20professional%20headshot&width=300&height=300&seq=team-1&orientation=squarish',
    },
    {
      name: '金秀雅',
      role: '内容总监',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20woman%20with%20warm%20smile%2C%20elegant%20business%20casual%20look%2C%20modern%20creative%20office%20background%2C%20clean%20portrait%20photography%2C%20soft%20natural%20lighting%2C%20minimalist%20composition%2C%20professional%20headshot&width=300&height=300&seq=team-2&orientation=squarish',
    },
    {
      name: '朴正宇',
      role: '技术总监',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20man%20in%20smart%20casual%20attire%2C%20confident%20friendly%20expression%2C%20modern%20tech%20office%20background%2C%20clean%20portrait%20photography%2C%20soft%20natural%20lighting%2C%20minimalist%20composition%2C%20professional%20headshot&width=300&height=300&seq=team-3&orientation=squarish',
    },
    {
      name: '崔美英',
      role: '运营经理',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20woman%20with%20warm%20professional%20smile%2C%20creative%20office%20environment%2C%20clean%20portrait%20photography%2C%20soft%20natural%20lighting%2C%20minimalist%20composition%2C%20professional%20headshot&width=300&height=300&seq=team-4&orientation=squarish',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 韩国漫画网"
        description="韩国漫画网是专业的韩漫在线阅读平台，提供最新最全的韩漫免费阅读服务。了解我们的品牌故事、团队和联系方式。"
        keywords="韩国漫画网,关于韩国漫画网,韩漫平台,联系我们"
        canonicalUrl="https://www.koreacomic.cn/about"
      />
      <OrganizationSchema />
      <WebPageSchema
        title="关于我们 - 韩国漫画网"
        description="韩国漫画网是专业的韩漫在线阅读平台，提供最新最全的韩漫免费阅读服务。"
        url="https://www.koreacomic.cn/about"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Hero */}
            <section className="mt-6 text-center py-14">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900">
                关于韩国漫画网
              </h1>
              <p className="text-foreground-500 text-base mt-3 max-w-2xl mx-auto leading-relaxed">
                致力于打造最纯净的韩漫阅读平台，让每一位热爱韩漫的读者都能在这里找到属于自己的阅读天地。
              </p>
            </section>

            {/* Brand Story */}
            <section className="py-10">
              <div className="flex flex-col lg:flex-row gap-10 items-center">
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      韩国漫画网创立于2024年，由一群热爱韩国漫画的年轻人共同创办。创始团队深耕韩漫领域多年，深知国内韩漫爱好者对高质量、无广告阅读体验的需求。
                    </p>
                    <p>
                      我们与韩国多家知名漫画平台和作者建立了深度合作关系，确保第一时间为国内读者带来最新、最优质的韩漫作品。所有作品均经过正版授权或合法转载，保障了内容的质量与合规性。
                    </p>
                    <p>
                      韩国漫画网秉承"人人都有享受好故事的权利"的理念，坚持免费阅读模式，让每一位读者都能无门槛地享受韩漫的魅力。
                    </p>
                    <p>
                      我们的编辑团队每日精心筛选和推荐优质漫画，覆盖恋爱、奇幻、动作、悬疑、搞笑、日常等多个类型，满足不同读者的阅读偏好。从热血青春到浪漫爱情，从悬疑推理到治愈日常，总有一部作品能触动你的心弦。
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[400px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20comic%20art%20on%20walls%2C%20warm%20ambient%20lighting%2C%20team%20collaboration%20area%2C%20bookshelves%20with%20manhwa%20collections%2C%20cozy%20and%20inspiring%20atmosphere%2C%20clean%20minimalist%20design%2C%20professional%20interior%20photography%2C%20soft%20natural%20light%20through%20windows&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="韩国漫画网办公环境"
                    className="w-full rounded-lg object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="py-10">
              <div className="bg-primary-500 rounded-2xl p-8 md:p-12">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  {[
                    { value: '20,000+', label: '漫画作品' },
                    { value: '580万+', label: '累计读者' },
                    { value: '99.8%', label: '用户满意度' },
                    { value: '24/7', label: '不间断更新' },
                  ].map((stat, idx) => (
                    <div key={idx}>
                      <p className="text-3xl md:text-4xl font-heading font-bold text-background-50">{stat.value}</p>
                      <p className="text-sm text-primary-100 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                    <i className="ri-focus-2-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    为全球中文读者提供最全面、最优质的韩漫阅读体验。我们致力于打破语言和文化壁垒，让优秀的韩国漫画作品触达更多读者。通过不断优化的技术平台和精心策划的内容，我们希望成为连接韩国漫画创作者与中文读者之间的桥梁。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                    <i className="ri-lightbulb-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    成为中文世界最具影响力的韩漫阅读平台。我们不仅提供漫画阅读服务，更希望打造一个充满活力的漫画社区，让读者可以分享阅读心得、交流观后感，形成独特的韩漫文化圈。未来，我们还将拓展原创漫画孵化业务，支持更多优秀漫画创作者的成长。
                  </p>
                </div>
              </div>
            </section>

            {/* Team */}
            <section className="py-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                核心团队
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                {teamMembers.map((member) => (
                  <div key={member.name} className="text-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 mx-auto rounded-full overflow-hidden bg-background-200 mb-3">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="text-sm font-medium text-foreground-900">{member.name}</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">{member.role}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="py-10">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 mb-6">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。我们会在1-2个工作日内回复您。
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-500">contact@koreacomic.cn</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 flex-shrink-0">
                        <i className="ri-customer-service-2-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">在线客服</p>
                        <p className="text-sm text-foreground-500">工作日 9:00 - 18:00（北京时间）</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">公司地址</p>
                        <p className="text-sm text-foreground-500">中国北京市朝阳区望京SOHO T3</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 bg-background-100 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                    在线留言
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="py-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                常见问题
              </h2>
              <FAQSchema />
              <div className="max-w-3xl mx-auto grid gap-3">
                {[
                  {
                    q: '韩国漫画网的漫画都是免费的吗？',
                    a: '是的！韩国漫画网上的所有漫画作品均可免费在线阅读全集，我们承诺永久免费，无任何隐藏收费。只需访问网站即可畅享全部内容。',
                  },
                  {
                    q: '韩国漫画网有手机App吗？',
                    a: '目前我们提供移动端优化的网页版，您可以在手机浏览器中直接访问，体验与App一样流畅。我们正在开发官方App，敬请期待。',
                  },
                  {
                    q: '漫画更新频率是怎样的？',
                    a: '我们与韩国各大漫画平台保持同步更新，热门连载作品通常每周更新一到两话。具体更新时间视作品而定，您可以在作品详情页查看更新情况。',
                  },
                  {
                    q: '如何支持韩国漫画网？',
                    a: '您可以通过分享我们的网站给更多朋友来支持我们。如果您有合作意向或建议，欢迎通过联系表单与我们沟通。',
                  },
                  {
                    q: '网站上的漫画有版权吗？',
                    a: '韩国漫画网上的所有漫画均经过正版授权或合法转载。我们严格遵守版权法规，尊重每一位创作者的劳动成果。',
                  },
                  {
                    q: '如何联系我们进行商务合作？',
                    a: '欢迎通过我们的联系邮箱 contact@koreacomic.cn 或在线留言表单与我们取得联系。我们会在1-2个工作日内回复商务合作相关咨询。',
                  },
                ].map((faq, idx) => (
                  <details key={idx} className="bg-background-100 rounded-lg p-4 cursor-pointer group">
                    <summary className="text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                      {faq.q}
                      <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                    </summary>
                    <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}